import {
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Link,
  Select,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import { handleHookFormHTTPError } from "src/common/form";
import { useKy } from "src/common/ky";
import { Loading } from "src/common/Loading";
import { Intercom } from "src/common/types";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderBackLink,
  PageHeaderTitle,
} from "src/layout/PageHeader";
import { INTERCOMS_URL } from "src/routes/ProductUnits/Intercoms/index";
import { IntercomComponentsDetails } from "src/routes/ProductUnits/Intercoms/Intercom/IntercomComponents/IntercomComponentsDetails";

interface IntercomValues {
  intercomModelId: string;
  serialNumber: string;
  notes: string;
  entryPointId: string;
}

const IntercomValidationSchema = Yup.object().shape({
  intercomModelId: Yup.string().required().label("Intercom Model"),
  serialNumber: Yup.string().required().label("Serial Number"),
  notes: Yup.string().label("Notes"),
  entryPointId: Yup.string().required().label("Entry Point ID"),
});

export const IntercomEdit = () => {
  const { id } = useParams<"id">();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
    reset,
    setError,
    getValues,
  } = useForm<IntercomValues>({
    defaultValues: {
      intercomModelId: "",
      serialNumber: "",
      notes: "",
      entryPointId: "",
    },
    resolver: yupResolver(IntercomValidationSchema),
  });

  const navigate = useNavigate();
  const toast = useToast();
  const ky = useKy();
  const updateIntercom = useMutation(
    (values: IntercomValues) =>
      ky.patch(`intercoms/${id}`, {
        json: { notes: values.notes, entryPointId: values.entryPointId },
      }),
    {
      onError: handleHookFormHTTPError(setError, getValues, toast),
      onSuccess: () => {
        toast({
          description: "Intercom successfully updated",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        navigate(INTERCOMS_URL);
      },
    }
  );

  const intercomQuery = useQuery<Intercom>(
    ["INTERCOM", id],
    () => ky.get(`intercoms/${id}`).json<Intercom>(),
    {
      onSuccess: (data) => {
        if (!isDirty) {
          reset({
            intercomModelId: data.intercomModelId,
            serialNumber: data.serialNumber,
            notes: data.notes,
            entryPointId: data.entryPointId || "",
          });
        }
      },
    }
  );

  if (intercomQuery.isLoading || !intercomQuery.data) {
    return <Loading />;
  }

  return (
    <>
      <PageHeader>
        <PageHeaderBackLink>
          <Link as={RouterLink} to={INTERCOMS_URL}>
            &#8249; Back to list
          </Link>
        </PageHeaderBackLink>
        <PageHeaderTitle>Edit Intercom</PageHeaderTitle>
      </PageHeader>
      <PageContent>
        <form
          onSubmit={handleSubmit((data: IntercomValues) =>
            updateIntercom.mutate(data)
          )}
        >
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem colSpan={1}>
              <VStack spacing={4}>
                <Controller
                  name="intercomModelId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isRequired
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                      isReadOnly
                      isDisabled
                    >
                      <FormLabel htmlFor="intercomModelId">
                        Intercom Model
                      </FormLabel>
                      <Select {...field} disabled id="intercomModelId">
                        <option value={intercomQuery.data.intercomModelId}>
                          {intercomQuery.data.intercomModel}
                        </option>
                      </Select>
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  name="serialNumber"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isRequired
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                      isReadOnly
                      isDisabled
                    >
                      <FormLabel htmlFor="serialNumber">
                        Serial Number
                      </FormLabel>
                      <Input {...field} disabled id="serialNumber" />
                    </FormControl>
                  )}
                />
                <Controller
                  name="entryPointId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isRequired
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="entryPointId">
                        Entry Point ID
                      </FormLabel>
                      <Input {...field} id="entryPointId" />
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  name="notes"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="notes">Notes</FormLabel>
                      <Textarea {...field} id="notes" />
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <FormControl isDisabled={true}>
                  <FormLabel htmlFor="authToken">Auth Token</FormLabel>
                  <Input
                    value={intercomQuery.data.authToken}
                    id="authToken"
                    disabled
                  />
                </FormControl>
              </VStack>
            </GridItem>

            <GridItem colSpan={2}>
              <Divider />
            </GridItem>

            <GridItem colSpan={2}>
              <ButtonGroup display="flex" justifyContent="flex-end">
                <Link as={RouterLink} to={INTERCOMS_URL}>
                  <Button>Cancel</Button>
                </Link>
                <Button
                  type="submit"
                  colorScheme="brand.blue"
                  isLoading={isSubmitting}
                >
                  Save Intercom
                </Button>
              </ButtonGroup>
            </GridItem>
          </Grid>
        </form>
      </PageContent>

      {intercomQuery.data.intercomActivationId &&
        intercomQuery.data.intercomModel.includes("Gen4") && (
          <PageContent>
            <Heading size={"md"}>Components</Heading>
            <IntercomComponentsDetails
              intercomActivationId={intercomQuery.data.intercomActivationId}
            />
          </PageContent>
        )}
    </>
  );
};
